// src/hooks/useFeed.ts
//
// The Discover feed listener. Subscribes to /users/{uid}/feed/ filtered
// to items that should be shown (unseen or seen, not swiped yet, not
// in cooldown). Exposes a swipe mutation that fires the tasteFeedback
// Cloud Function with optimistic local state, and a one-step undo
// buffer.

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { useUserPreferences } from './useUserPreferences';
import { useLocation } from './useLocation';

const FEEDBACK_URL =
  'https://us-central1-travelx-46b7a.cloudfunctions.net/tasteFeedback';

/** Recompute distance/walk-time once the user has drifted this many
 *  meters from the position we last computed against. Avoids
 *  thrashing the entire feed list on every GPS jiggle while still
 *  catching meaningful movement (one block ≈ 80m). */
const RECOMPUTE_THRESHOLD_M = 50;

/** Average comfortable walking pace, used to derive walk_time_min
 *  from haversine distance when the server-side Directions value
 *  has gone stale. ~70 m/min is a typical urban-walk estimate
 *  including stops and turns. */
const WALK_PACE_M_PER_MIN = 70;

/** Minimum Google Places rating for a card to be shown. Items
 *  without a known rating (LLM-knowledge picks, places with too
 *  few reviews to have a Google rating) pass through — we only
 *  drop items whose rating is *known* to fall below the bar.
 *  Notification items also bypass this gate. */
const MIN_RATING = 4;

function haversineM(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export type SwipeAction = 'match' | 'super_match' | 'pass';
export type SwipeStatus = 'unseen' | 'seen' | 'pass' | 'match' | 'super_match';

export interface FeedItem {
  id: string;
  source: string;
  created_at: Date | null;
  expires_at: Date | null;
  title: string;
  description: string;
  image_url?: string | null;
  location: {
    lat: number;
    lng: number;
    address?: string | null;
  };
  distance_m?: number;
  walk_time_min?: number;
  tags: string[];
  place_id?: string;
  chat_prompt: string;
  rationale?: string;
  urgency: 'low' | 'normal' | 'high';
  is_notification: boolean;
  /** "places" (default) or "llm-knowledge" — controls placeholder rendering */
  origin?: 'places' | 'llm-knowledge';
  /** Carousel of Place Photos URLs, up to 5. */
  photo_urls?: string[];
  /** Average rating 1-5 from Google Places. */
  rating?: number;
  /** Total user ratings count on Google Places. */
  user_ratings_total?: number;
  /** Google's editorial summary of the place. */
  editorial_summary?: string;
  /** Whether Google reports it's currently open. */
  is_open_now?: boolean;
  /** 0-4. Price level from Google. */
  price_level?: number;
  swipe_status: SwipeStatus;
  swiped_at?: Date | null;
}

interface UndoEntry {
  item: FeedItem;
  action: SwipeAction;
  at: number;
}

/**
 * Feed state. Items are already filtered to "show me": unseen/seen,
 * and (for passes) past cooldown. Match/pass state is applied
 * optimistically for responsive UI.
 */
export const useFeed = () => {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Optimistic overlay: swipe_status changes we've sent to the server
  // but haven't yet seen reflected in the Firestore listener. Keyed by
  // item id.
  const optimisticStatusRef = useRef<Map<string, SwipeStatus>>(new Map());

  // One-step undo stack. Keeps the last swiped item so user can bring
  // it back.
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([]);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setIsLoading(false);
      return;
    }

    const ref = collection(db, 'users', user.uid, 'feed');
    // Firestore composite query: swipe_status in unseen/seen only.
    // Pass items reappear only after cooldown expires; the generator
    // handles cooldown by not re-adding them until then. We filter
    // client-side below for any stragglers.
    const q = query(
      ref,
      where('swipe_status', 'in', ['unseen', 'seen']),
      orderBy('created_at', 'desc')
    );

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const now = Date.now();
        const out: FeedItem[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data();
          // Filter out expired items
          const expiresAt =
            data.expires_at instanceof Timestamp
              ? data.expires_at.toDate()
              : null;
          if (expiresAt && expiresAt.getTime() < now) return;

          const optimisticOverride = optimisticStatusRef.current.get(doc.id);
          const effectiveStatus: SwipeStatus =
            optimisticOverride || data.swipe_status || 'unseen';

          // Re-filter if optimistic override has moved this out of the feed
          if (
            effectiveStatus !== 'unseen' &&
            effectiveStatus !== 'seen'
          ) {
            return;
          }

          out.push({
            id: doc.id,
            source: data.source || 'guide',
            created_at:
              data.created_at instanceof Timestamp
                ? data.created_at.toDate()
                : null,
            expires_at: expiresAt,
            title: data.title || '',
            description: data.description || '',
            image_url: data.image_url,
            location: data.location || { lat: 0, lng: 0 },
            distance_m: data.distance_m,
            walk_time_min: data.walk_time_min,
            tags: Array.isArray(data.tags) ? data.tags : [],
            place_id: data.place_id,
            chat_prompt: data.chat_prompt || '',
            rationale: data.rationale,
            urgency: data.urgency || 'normal',
            is_notification: !!data.is_notification,
            origin:
              data.origin === 'llm-knowledge' ? 'llm-knowledge' : 'places',
            photo_urls: Array.isArray(data.photo_urls)
              ? (data.photo_urls as string[])
              : undefined,
            rating: typeof data.rating === 'number' ? data.rating : undefined,
            user_ratings_total:
              typeof data.user_ratings_total === 'number'
                ? data.user_ratings_total
                : undefined,
            editorial_summary:
              typeof data.editorial_summary === 'string'
                ? data.editorial_summary
                : undefined,
            is_open_now:
              typeof data.is_open_now === 'boolean'
                ? data.is_open_now
                : undefined,
            price_level:
              typeof data.price_level === 'number'
                ? data.price_level
                : undefined,
            swipe_status: effectiveStatus,
            swiped_at:
              data.swiped_at instanceof Timestamp
                ? data.swiped_at.toDate()
                : null,
          });
        });

        // Sort: notifications first, then newest. Fresh client-side
        // ordering since Firestore can't combine multiple orderBys with
        // our where filter easily.
        out.sort((a, b) => {
          if (a.is_notification !== b.is_notification) {
            return a.is_notification ? -1 : 1;
          }
          const aT = a.created_at?.getTime() || 0;
          const bT = b.created_at?.getTime() || 0;
          return bT - aT;
        });

        setItems(out);
        setIsLoading(false);
      },
      (err) => {
        console.warn('Feed listener error:', err);
        setError(err.message);
        setIsLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const swipe = useCallback(async (item: FeedItem, action: SwipeAction) => {
    const user = auth.currentUser;
    if (!user) return;

    // Optimistic: apply status immediately so the card disappears from
    // the deck without waiting for the server round-trip.
    const newStatus: SwipeStatus =
      action === 'match'
        ? 'match'
        : action === 'super_match'
        ? 'super_match'
        : 'pass';
    optimisticStatusRef.current.set(item.id, newStatus);

    // Optimistically remove from local items
    setItems((prev) => prev.filter((i) => i.id !== item.id));

    // Push onto undo stack (only the last one is retained)
    setUndoStack([{ item, action, at: Date.now() }]);

    try {
      const token = await user.getIdToken();
      fetch(FEEDBACK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          auth_token: token,
          feed_item_id: item.id,
          action,
        }),
      }).catch((err) => {
        console.warn('Swipe request failed:', err);
      });
    } catch (err) {
      console.warn('Swipe submit failed:', err);
    }
  }, []);

  const undoLastSwipe = useCallback(async () => {
    const last = undoStack[undoStack.length - 1];
    if (!last) return;
    const user = auth.currentUser;
    if (!user) return;

    // Remove from undo stack
    setUndoStack([]);

    // Remove from optimistic overlay — Firestore listener will bring
    // the item back as the server undoes it
    optimisticStatusRef.current.delete(last.item.id);

    // Optimistically put it back at the front of the deck
    setItems((prev) => [last.item, ...prev.filter((i) => i.id !== last.item.id)]);

    try {
      const token = await user.getIdToken();
      fetch(FEEDBACK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          auth_token: token,
          feed_item_id: last.item.id,
          action: 'undo',
        }),
      }).catch((err) => {
        console.warn('Undo request failed:', err);
      });
    } catch (err) {
      console.warn('Undo submit failed:', err);
    }
  }, [undoStack]);

  const canUndo = undoStack.length > 0;

  // Apply user's distance preference at the derive layer so the
  // listener doesn't have to re-run on slider changes. Items
  // without a `distance_m` (older items, or knowledge picks where
  // we couldn't compute exact distance) pass through — better to
  // show them than drop. Notification items also pass through
  // unconditionally; if we surfaced one to the user via push,
  // hiding it because of a tighter slider is confusing.
  const { prefs } = useUserPreferences();

  // Live position used to recompute distances. The server stamps a
  // `distance_m` on each feed item when it generates the card, but
  // that value is frozen at generation time — once the user walks
  // around, those numbers go stale and stale numbers fail the radius
  // gate in both directions (cards we should now hide stay visible,
  // and cards we should now show stay hidden too if the slider was
  // tight). We track a "stable" position that updates only when the
  // user has moved >RECOMPUTE_THRESHOLD_M, then recompute haversine
  // distance to every item against that.
  const { location } = useLocation();
  const [stablePos, setStablePos] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  useEffect(() => {
    if (!location?.latitude || !location?.longitude) return;
    const next = { lat: location.latitude, lng: location.longitude };
    if (!stablePos) {
      setStablePos(next);
      return;
    }
    const drift = haversineM(
      stablePos.lat,
      stablePos.lng,
      next.lat,
      next.lng
    );
    if (drift >= RECOMPUTE_THRESHOLD_M) {
      setStablePos(next);
    }
  }, [location?.latitude, location?.longitude, stablePos]);

  const filteredItems = useMemo(() => {
    const maxM = prefs.discoverRadiusKm * 1000;
    return items
      .map((it) => {
        // Recompute distance/walk-time against the user's CURRENT
        // location when we have one. Otherwise fall back to whatever
        // the server stamped at generation time.
        if (
          stablePos &&
          typeof it.location?.lat === 'number' &&
          typeof it.location?.lng === 'number'
        ) {
          const liveM = Math.round(
            haversineM(
              stablePos.lat,
              stablePos.lng,
              it.location.lat,
              it.location.lng
            )
          );
          return {
            ...it,
            distance_m: liveM,
            walk_time_min: Math.max(
              1,
              Math.round(liveM / WALK_PACE_M_PER_MIN)
            ),
          };
        }
        return it;
      })
      .filter(
        (it) =>
          it.is_notification ||
          it.distance_m === undefined ||
          it.distance_m <= maxM
      )
      .filter(
        // Rating gate — drop places we *know* are below the bar.
        // Items with no rating (LLM picks, places lacking review
        // volume on Google) and notifications always pass.
        (it) =>
          it.is_notification ||
          typeof it.rating !== 'number' ||
          it.rating >= MIN_RATING
      );
  }, [items, prefs.discoverRadiusKm, stablePos]);

  return {
    items: filteredItems,
    isLoading,
    error,
    swipe,
    undoLastSwipe,
    canUndo,
  };
};
